import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
    fetchPatientsGetById,
    fetchPatientsResolve,
} from '~/backend/connector-next-api/connector-next-components';
import { useConnectorNextContext } from '~/backend/connector-next-api/connector-next-context';
import { scanFiles } from '~/domain/dicom/importScan';
import { expandArchives } from '~/utils/io/files/archive';
import {
    checkFileSelection,
    collectDroppedFiles,
    withRelativePath,
} from '~/utils/io/files/fileTree';
import {
    DROP_WHILE_COMMITTING,
    IMPORT_LIMITS,
    alreadyStoredSeriesKeys,
    currentEntry,
    diskImportReducer,
    errorMessage,
    initialDiskImportState,
    queueOf,
    storedInstancesOf,
    verdictForResolution,
    type ClinicalContextField,
    type CommitRequest,
    type DiskImportState,
    type DiskStage,
    type ImportQueue,
    type QueueEntry,
} from '../model';
import { abortableTask } from './abortableTask';
import { useImportCommit } from './useImportCommit';

/** Files between progress dispatches. A per-file dispatch re-renders the dialog once per file. */
const PROGRESS_STEP = 25;

// webkitdirectory is exclusive (folders only) and untyped by React; no accept, since DICOM is often extension-less.
const FOLDER_PICKER_ATTRIBUTES = {
    webkitdirectory: '',
    directory: '',
} as Record<string, string>;

export { FOLDER_PICKER_ATTRIBUTES };

export type DiskImport = {
    readonly state: DiskImportState;
    readonly stage: DiskStage;
    readonly queue: ImportQueue | undefined;
    readonly entry: QueueEntry | undefined;
    readonly committing: boolean;
    /** Reviewing, or committing the entry being reviewed: both show the review pane. */
    readonly reviewOpen: boolean;
    /** The frozen request of the commit in flight, if one is. */
    readonly inFlight: CommitRequest | undefined;
    /** The "This computer" source line in the rail. */
    readonly subtitle: string;
    readonly isDragging: boolean;
    readonly dropHandlers: {
        readonly onDragOver: (event: React.DragEvent) => void;
        readonly onDragLeave: (event: React.DragEvent) => void;
        readonly onDrop: (event: React.DragEvent) => void;
    };
    readonly picker: {
        readonly fileInputRef: React.RefObject<HTMLInputElement>;
        readonly folderInputRef: React.RefObject<HTMLInputElement>;
        readonly onPick: (event: React.ChangeEvent<HTMLInputElement>) => void;
        readonly chooseFiles: () => void;
        readonly chooseFolder: () => void;
    };
    readonly actions: {
        /** Abandons the current lifetime, so every effect's cleanup aborts. */
        readonly restart: () => void;
        readonly selectEntry: (index: number) => void;
        readonly toggleSeries: (seriesKey: string) => void;
        readonly changeContext: (
            field: ClinicalContextField,
            value: string
        ) => void;
        readonly requestCommit: () => void;
        readonly skipEntry: () => void;
        readonly recheck: () => void;
        readonly undoSkip: () => void;
    };
};

/**
 * The disk import's whole application layer: the state machine, the effects that feed it, and
 * the input handlers. Owns only result-producing effects, each tagged by generation so a result
 * belonging to an abandoned drop is dropped by the reducer.
 */
export function useDiskImport(open: boolean): DiskImport {
    const intl = useIntl();
    const [disk, dispatch] = useReducer(
        diskImportReducer,
        0,
        initialDiskImportState
    );
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    // Sole generation allocator, outside the reducer: StrictMode double-invokes reducers.
    const generationRef = useRef(0);
    const nextGeneration = useCallback(() => (generationRef.current += 1), []);

    // Kept in a ref: the context hook returns a new object each render.
    const { fetcherOptions } = useConnectorNextContext();
    const fetcherOptionsRef = useRef(fetcherOptions);
    fetcherOptionsRef.current = fetcherOptions;

    const commit = useImportCommit();
    const startedRequestRef = useRef<CommitRequest | undefined>(undefined);

    const generation = disk.generation;
    const queue = queueOf(disk.stage);
    const entry = currentEntry(disk);
    const committing = disk.stage.kind === 'committing';

    const restart = () => {
        dispatch({ type: 'restarted', generation: nextGeneration() });
    };

    const onDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        dispatch({ type: 'drag-changed', isDragging: true });
    };

    const onDragLeave = (event: React.DragEvent) => {
        // dragleave bubbles from every child, so only the pointer leaving the dialog clears it.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
        }
        dispatch({ type: 'drag-changed', isDragging: false });
    };

    const onDrop = (event: React.DragEvent) => {
        // preventDefault first: a drop left to the browser default navigates the SPA away.
        event.preventDefault();
        dispatch({ type: 'drag-changed', isDragging: false });
        if (committing) {
            dispatch({ type: 'drop-refused', message: DROP_WHILE_COMMITTING });
            return;
        }
        const offer = nextGeneration();
        dispatch({ type: 'collect-started', offer });
        // Nothing may be awaited before this call: the DataTransfer is neutered once the handler returns.
        void collectDroppedFiles(event.dataTransfer.items, IMPORT_LIMITS)
            .then((result) =>
                dispatch({ type: 'collect-settled', offer, result })
            )
            .catch(() =>
                dispatch({
                    type: 'drop-refused',
                    offer,
                    message: {
                        id: 'patientList.import.refusal.unreadableDrop',
                    },
                })
            );
    };

    const onPick = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Renamed so a picked folder and a dropped one reach the scan under the same names.
        const files = [...(event.target.files ?? [])].map(withRelativePath);
        // Cleared so picking the same folder twice in a row still fires a change event.
        event.target.value = '';
        if (committing) {
            dispatch({ type: 'drop-refused', message: DROP_WHILE_COMMITTING });
            return;
        }
        const offer = nextGeneration();
        dispatch({ type: 'collect-started', offer });
        dispatch({
            type: 'collect-settled',
            offer,
            result: checkFileSelection(files, IMPORT_LIMITS),
        });
    };

    const expandingFiles =
        disk.stage.kind === 'expanding' ? disk.stage.files : undefined;

    useEffect(() => {
        if (!open || expandingFiles === undefined) {
            return;
        }
        return abortableTask(async (signal, settle) => {
            const result = await expandArchives(
                [...expandingFiles],
                IMPORT_LIMITS,
                {
                    signal,
                    onProgress: (produced) => {
                        // An archive's total is unknown, so the first file stands in for it:
                        // without it a drop smaller than one step would read 0 throughout.
                        if (produced === 1 || produced % PROGRESS_STEP === 0) {
                            dispatch({
                                type: 'expand-progress',
                                generation,
                                produced,
                            });
                        }
                    },
                }
            );
            // An abandoned expansion is discarded, not refused: its refusal would land on the next drop.
            settle(() =>
                dispatch({ type: 'expand-settled', generation, result })
            );
        });
    }, [expandingFiles, generation, open]);

    const scanningFiles =
        disk.stage.kind === 'scanning' ? disk.stage.files : undefined;

    useEffect(() => {
        if (!open || scanningFiles === undefined) {
            return;
        }
        return abortableTask(async (signal, settle) => {
            const scan = await scanFiles([...scanningFiles], {
                signal,
                onProgress: (scanned, total) => {
                    if (scanned % PROGRESS_STEP === 0 || scanned === total) {
                        dispatch({
                            type: 'scan-progress',
                            generation,
                            scanned,
                            total,
                        });
                    }
                },
            });
            // An aborted scan returns a partial result; accepting it as complete would show a queue missing patients.
            settle(() => dispatch({ type: 'scan-settled', generation, scan }));
        });
    }, [generation, open, scanningFiles]);

    // Primitives only, so the check restarts exactly when the entry it is about changes.
    const reviewQueue =
        disk.stage.kind === 'reviewing' ? disk.stage.queue : undefined;
    const checkTarget =
        reviewQueue === undefined
            ? undefined
            : reviewQueue.entries[reviewQueue.current];
    const resolveIndex =
        reviewQueue !== undefined &&
        checkTarget !== undefined &&
        checkTarget.status.kind === 'waiting' &&
        checkTarget.verdict.kind === 'checking'
            ? reviewQueue.current
            : -1;
    const resolvePatientId =
        resolveIndex < 0 || checkTarget === undefined
            ? ''
            : checkTarget.drop.identity.dicomPatientId;

    useEffect(() => {
        if (!open || resolveIndex < 0) {
            return;
        }
        // The generated hook's mutationFn drops the AbortSignal, so the fetcher is called directly.
        return abortableTask(async (signal, settle) => {
            try {
                const resolution = await fetchPatientsResolve(
                    {
                        ...fetcherOptionsRef.current,
                        body: { dicomPatientId: resolvePatientId },
                        // A read sent as POST; replaying it changes nothing.
                        retryPolicy: 'transient',
                    },
                    signal
                );
                settle(() =>
                    dispatch({
                        type: 'verdict-settled',
                        generation,
                        entryIndex: resolveIndex,
                        dicomPatientId: resolvePatientId,
                        verdict: verdictForResolution(resolution),
                    })
                );
            } catch (error) {
                settle(() =>
                    dispatch({
                        type: 'verdict-settled',
                        generation,
                        entryIndex: resolveIndex,
                        dicomPatientId: resolvePatientId,
                        verdict: {
                            kind: 'failed',
                            message: errorMessage(
                                error,
                                'patientList.import.verdict.checkFailed'
                            ),
                        },
                    })
                );
            }
        });
    }, [generation, open, resolveIndex, resolvePatientId]);

    // Which of the drop's datasets the matched record already holds; asked only for a resolved existing patient.
    const storedTarget =
        reviewQueue !== undefined &&
        checkTarget !== undefined &&
        checkTarget.status.kind === 'waiting' &&
        checkTarget.verdict.kind === 'resolved' &&
        checkTarget.verdict.target.kind === 'existing-patient'
            ? {
                  index: reviewQueue.current,
                  patientId: checkTarget.verdict.target.patientId,
                  drop: checkTarget.drop,
              }
            : undefined;
    const storedIndex = storedTarget?.index ?? -1;
    const storedPatientId = storedTarget?.patientId ?? '';
    const storedDrop = storedTarget?.drop;

    useEffect(() => {
        if (!open || storedIndex < 0 || storedDrop === undefined) {
            return;
        }
        return abortableTask(async (signal, settle) => {
            try {
                const detail = await fetchPatientsGetById(
                    {
                        ...fetcherOptionsRef.current,
                        pathParams: { id: storedPatientId },
                    },
                    signal
                );
                settle(() =>
                    dispatch({
                        type: 'stored-datasets-settled',
                        generation,
                        entryIndex: storedIndex,
                        dicomPatientId: storedDrop.identity.dicomPatientId,
                        stored: alreadyStoredSeriesKeys(
                            storedDrop,
                            storedInstancesOf(detail)
                        ),
                    })
                );
            } catch {
                // Silent: a record that could not be read marks nothing and withholds nothing from the import.
            }
        });
    }, [generation, open, storedDrop, storedIndex, storedPatientId]);

    const commitRequest =
        disk.stage.kind === 'committing' ? disk.stage.request : undefined;

    useEffect(() => {
        // Frozen request identity holds across phase updates; the ref keeps this firing once per commit.
        if (commitRequest === undefined) {
            return;
        }
        if (startedRequestRef.current === commitRequest) {
            return;
        }
        startedRequestRef.current = commitRequest;
        void (async () => {
            const result = await commit(
                commitRequest.instances,
                commitRequest.context,
                (phase) => dispatch({ type: 'commit-phase', generation, phase })
            );
            dispatch({ type: 'commit-settled', generation, result });
        })();
    }, [commit, commitRequest, generation]);

    useEffect(() => {
        if (!open) {
            return;
        }
        // A file dropped outside the dialog is swallowed, else the browser navigates away from the queue.
        const swallow = (event: Event) => event.preventDefault();
        document.addEventListener('dragover', swallow);
        document.addEventListener('drop', swallow);
        return () => {
            document.removeEventListener('dragover', swallow);
            document.removeEventListener('drop', swallow);
        };
    }, [open]);

    const subtitle =
        disk.pendingOffer !== undefined
            ? intl.formatMessage({
                  id: 'patientList.import.source.readingDrop',
              })
            : queue !== undefined
              ? intl.formatMessage(
                    { id: 'patientList.import.source.queueSummary' },
                    {
                        fileCount: queue.entries.reduce(
                            (n, each) => n + each.drop.instanceCount,
                            0
                        ),
                        patientCount: queue.entries.length,
                    }
                )
              : disk.stage.kind === 'expanding'
                ? intl.formatMessage({
                      id: 'patientList.import.source.expanding',
                  })
                : disk.stage.kind === 'scanning'
                  ? intl.formatMessage({
                        id: 'patientList.import.source.scanning',
                    })
                  : intl.formatMessage({
                        id: 'patientList.import.source.idle',
                    });

    return {
        state: disk,
        stage: disk.stage,
        queue,
        entry,
        committing,
        reviewOpen: disk.stage.kind === 'reviewing' || committing,
        inFlight: commitRequest,
        subtitle,
        isDragging: disk.isDragging,
        dropHandlers: { onDragOver, onDragLeave, onDrop },
        picker: {
            fileInputRef,
            folderInputRef,
            onPick,
            chooseFiles: () => fileInputRef.current?.click(),
            chooseFolder: () => folderInputRef.current?.click(),
        },
        actions: {
            restart,
            selectEntry: (index: number) =>
                dispatch({ type: 'entry-selected', index }),
            toggleSeries: (seriesKey: string) =>
                dispatch({ type: 'series-toggled', seriesKey }),
            changeContext: (field: ClinicalContextField, value: string) =>
                dispatch({ type: 'context-changed', field, value }),
            requestCommit: () => dispatch({ type: 'commit-requested' }),
            skipEntry: () => dispatch({ type: 'entry-skipped' }),
            recheck: () => dispatch({ type: 'recheck-requested' }),
            undoSkip: () => dispatch({ type: 'skip-undone' }),
        },
    };
}
